import { date } from 'quasar'
import * as dateConsts from '@/consts/date'

const today = new Date()
const year = today.getFullYear()
const prevYear = year - 1

const { formatDate } = date

/**
 * Récupère les chiffres d'une date format iso
 * @type {RegExp}
 */
const regIsoParse = /[^0-9]/

/**
 * Parse une date format iso vers un objet date JS
 * @param s
 */
export const parseIso = (s = '') => {
  if (!s) {
    return null
  }
  if (typeof s !== 'string') {
    // devLog('warn', 'parseIso with s : ', s)
    return new Date(s)
  }
  const [year = 0, month = 0, date = 0, hours = 0, minutes = 0, seconds = 0, ms = 0] = s.split(regIsoParse)
  return new Date(year, month - 1, date, hours, minutes, seconds, ms)
}

export const dateFormatFr = (d) => formatDate(parseIso(d), dateConsts.DATE_FR)
export const dateFormatFrJourMoisLong = (d) => formatDate(parseIso(d), dateConsts.DATE_FR_JOUR_MOIS_LONG)
export const dateFormatFrMoisAnneeLong = (d) => formatDate(parseIso(d), dateConsts.DATE_FR_MONTH_YEAR_LONG)
export const dateFormatFrLong = (d) => formatDate(parseIso(d), dateConsts.DATE_FR_LONG)

export const dateTimeFormatFr = (d) => formatDate(parseIso(d), dateConsts.DATETIME_FR)
export const dateTimeFormatFrLong = (d) => formatDate(parseIso(d), dateConsts.DATETIME_FR_LONG)

export const dateTimeFormatIso = (d) => formatDate(parseIso(d), dateConsts.DATETIME_ISO)
export const dateFormatIso = (d) => formatDate(parseIso(d), dateConsts.DATE_ISO)
export const dateFormatIsoYearMonth = (d) => formatDate(parseIso(d), dateConsts.DATE_ISO_YEAR_MONTH)

export const dateFormatQuasar = (d) => formatDate(parseIso(d), dateConsts.DATE_QUASAR)

export const timeFormat = (d) => formatDate(parseIso(d), dateConsts.TIME)
export const timeFormatLong = (d) => formatDate(parseIso(d), dateConsts.TIME_LONG)
export const timeFullFormat = (d) => formatDate(parseIso(d), dateConsts.TIME_FULL)

export const minutesFormatHm = (minutes) => {
  return minutes > 0
    ? [Math.floor(minutes / 60), Math.ceil(minutes % 60)].map((v) => _.padStart(v, 2, '0')).join('h')
    : '0h00'
}

export const minutesDiff = (d1, d2) => d1 && d2 ? date.getDateDiff(d1, d2, 'minutes') : 0
export const hoursDiff = (d1, d2) => date.getDateDiff(d1, d2, 'hours')
export const daysDiff = (d1, d2) => date.getDateDiff(d1, d2, 'days')
export const daysDiffToday = (d1) => date.getDateDiff(d1, today, 'days')

export const secondesFormatHms = (secondes) => {
  if (!secondes) {
    return 0
  }
  const h = Math.floor(secondes / 3600)
  const m = Math.floor((secondes / 60) - h * 60)
  return [h, m, Math.floor(secondes % 60)].map((v) => _.padStart(Math.abs(v), 2, '0')).join(':')
}

const realDay = today.getDay() || 7 // Lundi 1 - Dimanche 7
const prevMonday = 1 - realDay
const nextSynday = 7 - realDay

const offsetFirstMonthDay = daysDiffToday(new Date(today.getFullYear(), today.getMonth(), 1))
const offsetLastMonthDay = daysDiffToday(new Date(today.getFullYear(), today.getMonth() + 1, 0))

export const RELATIVE_YESTERDAY = 'yesterday'
export const RELATIVE_TODAY = 'today'
export const RELATIVE_TOMOROW = 'tomorow'
export const RELATIVE_3NEXT_DAYS = '3NextDays'
export const RELATIVE_3PREV_DAYS = '3PrevDays'
export const RELATIVE_WEEK = 'week'
export const RELATIVE_SLIPPERY_WEEK = 'slipperyWeek'
export const RELATIVE_SLIPPERY_PREV_WEEK = 'slipperyPrevWeek'
export const RELATIVE_MONTH = 'month'
export const RELATIVE_TRIMESTER = 'trimester'
export const RELATIVE_SEMESTER = 'semester'
export const RELATIVE_SLIPPERY_PREV_MONTH = 'slipperyPrevMonth'
export const RELATIVE_SLIPPERY_MONTH = 'slipperyMonth'
export const RELATIVE_YEAR = 'year'
export const RELATIVE_PREV_YEAR = 'prevYear'
export const RELATIVE_CUSTOM = 'custom'

export const RELATIVE_OPTIONS = [
  { value: RELATIVE_YESTERDAY, label: 'La veille (J-1)', offsets: [{ days: -1 }, {}] },
  { value: RELATIVE_TODAY, label: 'Date du jour (J)', offsets: [{}, {}] },
  { value: RELATIVE_TOMOROW, label: 'Le lendemain (J+1)', offsets: [{}, { days: 1 }] },
  { value: RELATIVE_3NEXT_DAYS, label: '3 prochains jours (J+3)', offsets: [{}, { days: 3 }] },
  { value: RELATIVE_3PREV_DAYS, label: '3 derniers jours (J-3)', offsets: [{ days: -3 }, {}] },
  { value: RELATIVE_WEEK, label: 'Semaine courante', offsets: [{ days: prevMonday }, { days: nextSynday }] },
  { value: RELATIVE_SLIPPERY_WEEK, label: 'Semaine glissante (J+7)', offsets: [{}, { days: 7 }] },
  { value: RELATIVE_SLIPPERY_PREV_WEEK, label: 'Semaine précédente glissante (J-7)', offsets: [{ days: -7 }, {}] },
  {
    value: RELATIVE_MONTH,
    label: 'Mois courant',
    offsets: [{ days: offsetFirstMonthDay }, { days: offsetLastMonthDay }]
  },
  { value: RELATIVE_SLIPPERY_MONTH, label: 'Mois glissant (J+30)', offsets: [{}, { days: 30 }] },
  { value: RELATIVE_SLIPPERY_PREV_MONTH, label: 'Mois précédent glissant (J-30)', offsets: [{ days: -30 }, {}] },
  { value: RELATIVE_YEAR, label: 'Année courante', offsets: [year + '-01-01', year + '-12-31'] },
  { value: RELATIVE_PREV_YEAR, label: 'Année précédente (A-1)', offsets: [prevYear + '-01-01', prevYear + '-12-31'] },
  { value: RELATIVE_CUSTOM, label: 'Personnalisé' }
]

export const INDEXED_RELATIVE_OPTIONS = _.keyBy(RELATIVE_OPTIONS, 'value')

/**
 * Récupère une range relative depuis la liste des presets
 * @param v
 * @returns {*}
 */
export const getRelativeRange = (v) => _.get(INDEXED_RELATIVE_OPTIONS, [v, 'offsets'], [{}, {}])

/**
 * Retourne la date formatFr en deux parties [date, heure]
 * @param v
 * @returns {string[]}
 */
export const splitedDateFormatFr = (v) => v ? (dateTimeFormatFr(v) || '').split(' ') : ''

/**
 * Détermine la date du lundi de la semaine
 * @param d
 * @param {number} nbWeek - Nombre de semaines d'offset
 * @returns {Date}
 */
export const getPrevMonday = (d, nbWeek = 0) => {
  const day = d.getDay() || 7
  return date.addToDate(d, { days: (1 - day) - 7 * nbWeek })
}

/**
 * Détermine la date du dimanche de la semaine
 * @param d
 * @param {number} nbWeek - Nombre de semaines d'offset
 * @returns {Date}
 */
export const getNextSunday = (d, nbWeek = 0) => {
  const day = d.getDay() || 7
  return date.addToDate(d, { days: (7 - day) + 7 * nbWeek })
}
